// The widget's whole palette, derived from the business's one brand colour.
//
// Hover, soft, glow and container shades are `color-mix()` over the brand
// colour, so the browser does the blending. The few values where legibility
// is at stake are computed here instead, with the WCAG maths from the
// contract (`packages/contracts/src/widget/colour.ts` in the Aevo repo):
//
//   - `--primary-foreground`: black or white, whichever reads better on the
//     brand colour (`readableForeground`, same rule as the studio's readout).
//   - `--primary-text`: the brand colour as text on the panel background
//     (links, citations), darkened or lightened until it reaches 4.5:1.
//   - the dark theme's `--primary`: lightened until it reaches 3:1 against
//     the dark background, so a navy brand still shows as a button.
//
// The result is one CSS string, injected into the shadow root, with a light
// token set, a dark one, and `auto` following `prefers-color-scheme`.

import type { WidgetConfig } from './widget-config'

// ---------------------------------------------------------------------------
// WCAG maths (a copy of the contract's colour.ts, which the widget can't import)

function channel(hex: string, offset: number): number {
  const value = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance(hex: string): number {
  return 0.2126 * channel(hex, 1) + 0.7152 * channel(hex, 3) + 0.0722 * channel(hex, 5)
}

/** WCAG contrast ratio between two colours, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const [light, dark] = la >= lb ? [la, lb] : [lb, la]
  return (light + 0.05) / (dark + 0.05)
}

/** Black or white, whichever reads better on `background`. */
export function readableForeground(background: string): '#000000' | '#ffffff' {
  return contrastRatio(background, '#ffffff') >= contrastRatio(background, '#000000')
    ? '#ffffff'
    : '#000000'
}

// ---------------------------------------------------------------------------
// Mixing (sRGB, good enough for picking a shade that passes a contrast bar)

function toRgb(hex: string): [number, number, number] {
  return [1, 3, 5].map((i) => Number.parseInt(hex.slice(i, i + 2), 16)) as [number, number, number]
}

function toHex(rgb: readonly number[]): string {
  return `#${rgb.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`
}

/** `amount` of `b` mixed into `a` (0 = a, 1 = b). */
export function mixHex(a: string, b: string, amount: number): string {
  const ra = toRgb(a)
  const rb = toRgb(b)
  return toHex(ra.map((v, i) => v + (rb[i]! - v) * amount))
}

/**
 * `colour` moved toward `toward` in 5% steps until it reaches `ratio` against
 * `background`. Returns `toward` itself if nothing in between does.
 */
export function ensureContrast(colour: string, background: string, ratio: number, toward: string): string {
  for (let step = 0; step <= 20; step += 1) {
    const candidate = mixHex(colour, toward, step / 20)
    if (contrastRatio(candidate, background) >= ratio) return candidate
  }
  return toward
}

// ---------------------------------------------------------------------------
// Tokens

export interface PaletteTokens {
  [name: string]: string
}

/** Fixed neutrals; `{brand}` mixes in a hint of the brand colour. */
const LIGHT_BASE = {
  background: '#ffffff',
  foreground: '#0b1220',
}
const DARK_BASE = {
  background: '#0c0d10',
  foreground: '#f2f3f5',
}

/** The light theme in today's widget, kept exactly for embeds with no config. */
const LEGACY_LIGHT: PaletteTokens = {
  '--background': '#f8f9ff',
  '--foreground': '#0b1c30',
  '--card': '#f8f9ff',
  '--card-foreground': '#0b1c30',
  '--popover': '#f8f9ff',
  '--popover-foreground': '#0b1c30',
  '--muted': '#e5eeff',
  '--muted-foreground': '#434655',
  '--faint-foreground': '#737686',
  '--secondary': '#e5eeff',
  '--secondary-foreground': '#0b1c30',
  '--accent': '#dce9ff',
  '--accent-foreground': '#0b1c30',
  '--border': '#c3c6d7',
  '--input': '#c3c6d7',
}

export interface PaletteOptions {
  /** Today's widget: the old neutrals and white text on the brand colour. */
  legacy?: boolean
}

/** Light-theme tokens for a brand colour. */
export function lightTokens(brand: string, opts: PaletteOptions = {}): PaletteTokens {
  const background = opts.legacy ? '#f8f9ff' : mixHex(LIGHT_BASE.background, brand, 0.025)
  const surface = opts.legacy
    ? LEGACY_LIGHT
    : {
        '--background': background,
        '--foreground': mixHex(LIGHT_BASE.foreground, brand, 0.08),
        '--card': '#ffffff',
        '--card-foreground': 'var(--foreground)',
        '--popover': '#ffffff',
        '--popover-foreground': 'var(--foreground)',
        '--muted': 'color-mix(in oklab, var(--primary) 7%, #f4f4f5)',
        '--muted-foreground': 'color-mix(in oklab, var(--foreground) 66%, var(--background))',
        '--faint-foreground': 'color-mix(in oklab, var(--foreground) 48%, var(--background))',
        '--secondary': 'color-mix(in oklab, var(--primary) 7%, #f4f4f5)',
        '--secondary-foreground': 'var(--foreground)',
        '--accent': 'color-mix(in oklab, var(--primary) 12%, #f4f4f5)',
        '--accent-foreground': 'var(--foreground)',
        '--border': 'color-mix(in oklab, var(--foreground) 13%, var(--background))',
        '--input': 'color-mix(in oklab, var(--foreground) 18%, var(--background))',
      }
  return {
    ...surface,
    '--primary': brand,
    '--primary-foreground': opts.legacy ? '#ffffff' : readableForeground(brand),
    '--primary-hover': 'color-mix(in oklab, var(--primary) 88%, #000)',
    '--primary-soft': 'color-mix(in oklab, var(--primary) 10%, transparent)',
    '--primary-glow': 'color-mix(in oklab, var(--primary) 22%, transparent)',
    '--primary-container': 'color-mix(in oklab, var(--primary) 12%, var(--background))',
    '--primary-text': ensureContrast(brand, background, 4.5, '#000000'),
    '--ring': 'color-mix(in oklab, var(--primary) 55%, transparent)',
    '--destructive': '#dc2626',
    '--destructive-foreground': '#ffffff',
    '--warning': '#b45309',
    '--warning-surface': '#fef3c7',
    '--success': '#16a34a',
    '--shadow-color': 'rgb(15 23 42 / 0.10)',
  }
}

/** Dark-theme tokens for a brand colour. */
export function darkTokens(brand: string): PaletteTokens {
  const background = mixHex(DARK_BASE.background, brand, 0.04)
  const primary = ensureContrast(brand, background, 3, '#ffffff')
  return {
    '--background': background,
    '--foreground': DARK_BASE.foreground,
    '--card': mixHex('#16171b', brand, 0.05),
    '--card-foreground': 'var(--foreground)',
    '--popover': mixHex('#1b1c21', brand, 0.05),
    '--popover-foreground': 'var(--foreground)',
    '--muted': 'color-mix(in oklab, var(--primary) 9%, #1e1f24)',
    '--muted-foreground': 'color-mix(in oklab, var(--foreground) 68%, var(--background))',
    '--faint-foreground': 'color-mix(in oklab, var(--foreground) 50%, var(--background))',
    '--secondary': 'color-mix(in oklab, var(--primary) 9%, #1e1f24)',
    '--secondary-foreground': 'var(--foreground)',
    '--accent': 'color-mix(in oklab, var(--primary) 16%, #1e1f24)',
    '--accent-foreground': 'var(--foreground)',
    '--border': 'color-mix(in oklab, var(--foreground) 12%, var(--background))',
    '--input': 'color-mix(in oklab, var(--foreground) 16%, var(--background))',
    '--primary': primary,
    '--primary-foreground': readableForeground(primary),
    '--primary-hover': 'color-mix(in oklab, var(--primary) 86%, #fff)',
    '--primary-soft': 'color-mix(in oklab, var(--primary) 16%, transparent)',
    '--primary-glow': 'color-mix(in oklab, var(--primary) 30%, transparent)',
    '--primary-container': 'color-mix(in oklab, var(--primary) 20%, var(--background))',
    '--primary-text': ensureContrast(brand, background, 4.5, '#ffffff'),
    '--ring': 'color-mix(in oklab, var(--primary) 60%, transparent)',
    '--destructive': '#f87171',
    '--destructive-foreground': '#1a0606',
    '--warning': '#fbbf24',
    '--warning-surface': '#3a2a07',
    '--success': '#4ade80',
    '--shadow-color': 'rgb(0 0 0 / 0.45)',
  }
}

const RADIUS = { sharp: '0.375rem', soft: '0.875rem', round: '1.375rem' } as const
const LAUNCHER_SIZE = { sm: '3rem', md: '3.5rem', lg: '4rem' } as const

export const FONT_STACKS = {
  hanken:
    "'Hanken Grotesk', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  inter: "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  system: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  serif: "ui-serif, 'Iowan Old Style', 'Palatino Linotype', Georgia, Cambria, 'Times New Roman', serif",
} as const

/** Google Fonts stylesheet for fonts the visitor's device won't have. */
export const FONT_STYLESHEETS: Partial<Record<keyof typeof FONT_STACKS, string>> = {
  hanken: 'https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;500;600;700&display=swap',
  inter: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
}

function block(selector: string, tokens: PaletteTokens): string {
  const body = Object.entries(tokens)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n')
  return `${selector} {\n${body}\n}`
}

/**
 * The theme stylesheet for a config: shape/size variables on `.se-root`
 * (and `.se-portal`, where popovers render),
 * light tokens by default, dark tokens for `data-theme="dark"` and for
 * `data-theme="auto"` when the visitor prefers dark.
 */
export function buildThemeCss(config: WidgetConfig, opts: PaletteOptions = {}): string {
  const { appearance } = config
  const { placement, launcher } = appearance
  const layout: PaletteTokens = {
    '--radius': RADIUS[appearance.corners],
    '--se-font': FONT_STACKS[appearance.font],
    '--se-offset-x': `${placement.offsetX}px`,
    '--se-offset-y': `${placement.offsetY}px`,
    '--se-panel-width': `${placement.panelWidth}px`,
    '--se-panel-height': `${placement.panelHeight}px`,
    '--se-launcher-size': LAUNCHER_SIZE[launcher.size],
  }
  const light = lightTokens(appearance.brandColor, opts)
  const dark = darkTokens(appearance.brandColor)
  return [
    block('.se-root, .se-portal', { ...layout, ...light }),
    block('.se-root[data-theme="dark"], .se-portal[data-theme="dark"]', dark),
    `@media (prefers-color-scheme: dark) {\n${block('.se-root[data-theme="auto"], .se-portal[data-theme="auto"]', dark)}\n}`,
  ].join('\n')
}
